import { UserRole } from '@lombok/shared';
import { sessionUserFromIdToken } from './claims';
import type { AuthProvider, AuthSession, SignInInput, SignInResult } from './provider';

/**
 * DEV-ONLY auth provider ("modo desarrollo"). Used when no Cognito pool is
 * configured so `pnpm dev:web` runs with zero backend setup. It mints an
 * UNSIGNED JWT-shaped token carrying mock `custom:role`/`custom:*` claims, then
 * runs it through the SAME {@link sessionUserFromIdToken} parser as real auth —
 * so the session shape and code paths are identical to production.
 *
 * ⚠️ This token is NOT signed and grants NOTHING: the AWS backend rejects it
 * (signature check fails). It exists purely to make the UI shell usable locally.
 */
const STORAGE_KEY = 'lombok.devsession.v1';
const ONE_DAY_SECONDS = 24 * 60 * 60;

/** Base64url-encode a UTF-8 string (browser-safe, no padding). */
function base64url(input: string): string {
  const utf8 = unescape(encodeURIComponent(input));
  const b64 = typeof btoa === 'function' ? btoa(utf8) : Buffer.from(utf8, 'binary').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Build an unsigned (alg:none) JWT-shaped token with the given claims. */
function mintDevToken(claims: Record<string, unknown>): string {
  const header = base64url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64url(JSON.stringify(claims));
  return `${header}.${payload}.`; // empty signature segment — intentionally invalid.
}

interface StoredDev {
  email: string;
  role: UserRole;
}

/**
 * Relationship ids injected for demo roles so the SCOPED portals (sales/customer/
 * distributor) show the SEEDED data locally instead of an empty list. These ids match
 * services/api/src/seed/seed.ts (sales rep `sr_456`, customer `cus_123`). Dev-only —
 * real auth derives these from the verified Cognito JWT.
 */
function devRelationshipClaims(role: UserRole): Record<string, string> {
  switch (role) {
    case UserRole.SALES_REP:
    case UserRole.SALES_MANAGER:
      return { 'custom:salesRepId': 'sr_456' };
    case UserRole.CUSTOMER_USER:
      return { 'custom:customerId': 'cus_123', 'custom:allowedCustomerIds': 'cus_123' };
    case UserRole.DISTRIBUTOR_ADMIN:
    case UserRole.DISTRIBUTOR_USER:
      return { 'custom:distributorId': 'dist_demo' };
    default:
      return {};
  }
}

function buildSession(email: string, role: UserRole): AuthSession {
  const now = Math.floor(Date.now() / 1000);
  const expiresAt = now + ONE_DAY_SECONDS;
  const handle = email.split('@')[0] ?? 'Usuario';
  const idToken = mintDevToken({
    sub: `usr_${role}`,
    email,
    name: handle,
    'custom:role': role,
    ...devRelationshipClaims(role),
    locale: 'es-VE',
    iat: now,
    exp: expiresAt,
  });
  const user = sessionUserFromIdToken(idToken);
  // The parser always succeeds here (we just minted a valid role claim).
  return { user: user!, idToken, expiresAt };
}

export function createDevProvider(): AuthProvider {
  const read = (): StoredDev | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredDev) : null;
    } catch {
      return null;
    }
  };

  return {
    mode: 'dev',

    async restore() {
      const stored = read();
      return stored ? buildSession(stored.email, stored.role) : null;
    },

    async signIn({ email, password }: SignInInput): Promise<SignInResult> {
      // Role is smuggled through the password field by the dev LoginPage picker
      // as `role:<value>` so the AuthProvider contract stays role-agnostic.
      const match = /^role:(.+)$/.exec(password);
      const role = (match?.[1] as UserRole | undefined) ?? UserRole.ADMIN;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, role } satisfies StoredDev));
      return { status: 'ok', session: buildSession(email, role) };
    },

    async getValidIdToken() {
      const stored = read();
      return stored ? buildSession(stored.email, stored.role).idToken : null;
    },

    async signOut() {
      localStorage.removeItem(STORAGE_KEY);
    },
  };
}
